import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { NUM, TRY, fmtDate } from "@/lib/format";
import { Panel, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Pencil, Trash2, ClipboardList, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { STATUS } from "@/components/orders/orderFormUtils";

// Kayıtlı sipariş talep formları (tedarikçi · tarih · kalem · tahmini maliyet · durum)
export default function OrderFormsList({ forms, onEdit, onPreview, onChanged }) {
  const [open, setOpen] = useState(true);
  const setStatus = async (f, status) => {
    try {
      const { data } = await api.put(`/order-forms/${f.id}`, { status });
      toast.success(data.advanced_orders ? `Durum güncellendi · ${data.advanced_orders} talep 'Sipariş Verildi'` : "Durum güncellendi");
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (f) => {
    if (!window.confirm(`${f.form_no} silinsin mi?`)) return;
    try { await api.delete(`/order-forms/${f.id}`); toast.success("Sipariş formu silindi"); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Panel title="Sipariş Formları" subtitle="Tedarikçiye gönderilen teklif / sipariş isteme formları · tahmini maliyet yalnız ekranda" icon={ClipboardList} testid="order-forms-panel"
      actions={<button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="order-forms-toggle">{open ? <><ChevronUp className="h-3.5 w-3.5" />Gizle</> : <><ChevronDown className="h-3.5 w-3.5" />Göster ({forms.length})</>}</button>}>
      {!open ? null : forms.length === 0 ? (
        <Empty title="Henüz sipariş formu yok" desc="“Sipariş Çıktısı Oluştur” ile tedarikçiye gönderilecek ilk formu hazırlayın." />
      ) : (
        <div className="overflow-x-auto -mx-5 -mb-5">
          <table className="w-full min-w-[860px] text-[12px]" data-testid="order-forms-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b bg-slate-50/80">
                <th className="px-4 py-2 text-left">Form No</th><th className="px-3 py-2 text-left">Tarih</th><th className="px-3 py-2 text-left">Tedarikçi / Başlık</th>
                <th className="px-3 py-2 text-left">Marka</th><th className="px-3 py-2 text-right">Kalem</th><th className="px-3 py-2 text-right">Tahmini Maliyet (KDV hariç)</th>
                <th className="px-3 py-2 text-left">Durum</th><th className="px-3 py-2 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((f) => {
                const S = STATUS[f.status] || STATUS.taslak;
                return (
                  <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`order-form-${f.id}`}>
                    <td className="px-4 py-2 font-mono font-bold text-primary" data-testid={`order-form-no-${f.id}`}>{f.form_no}</td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{fmtDate(f.date)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{f.supplier_title || <span className="text-muted-foreground italic">Tedarikçi seçilmedi</span>}</div>
                      <div className="text-[10.5px] text-muted-foreground truncate max-w-[280px]">{f.title}</div>
                    </td>
                    <td className="px-3 py-2 text-[11px]">{(f.brands || []).slice(0, 3).join(", ")}{(f.brands || []).length > 3 ? " …" : ""}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{NUM(f.totals?.count)} <span className="text-muted-foreground">· {NUM(f.totals?.qty)}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold" data-testid={`order-form-net-${f.id}`}>{TRY(f.totals?.net)}<div className="text-[10px] font-normal text-muted-foreground">KDV dahil {TRY(f.totals?.total)}</div></td>
                    <td className="px-3 py-2">
                      <Select value={f.status || "taslak"} onValueChange={(v) => setStatus(f, v)}>
                        <SelectTrigger className={cn("h-7 w-[170px] text-[11px] font-semibold border", S.cls)} data-testid={`order-form-status-${f.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onPreview(f)} data-testid={`order-form-pdf-${f.id}`}><FileText className="h-3.5 w-3.5 mr-1" />PDF</Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(f)} title="Düzenle" data-testid={`order-form-edit-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => del(f)} title="Sil" data-testid={`order-form-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
